import { useTranslation, Trans } from "react-i18next";
import { PrimaryLink } from "../components/ui-blocks";

const ABOUT_IMG = {
  intro:
    "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/b15egufr_MTBL%20GROUP%20UAE%20%20final%20LOGO_20260424_140444_0000.png",
  values:
    "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/0gtgo90y_Our%20Core%20Value.png",
  mission:
    "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/vlzfjl2w_Our%20Mission.png",
  vision:
    "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/ip2qa800_1001199904.png",
  experience:
    "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/v2to7di7_Our%20Experience.png",
};

function Row({
  overline,
  title,
  children,
  img,
  alt,
  reverse = false,
  testId,
  imgContain = false,
}) {
  return (
    <section className="section-pad" data-testid={testId}>
      <div className="container-mtbl grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
          {overline && <p className="overline mb-5">{overline}</p>}
          <h2 className="text-[#0B1A3D] text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            {title}
          </h2>
          <div className="mt-6 space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
            {children}
          </div>
        </div>
        <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
          <div
            className={`rounded-2xl border border-slate-200 shadow-[0_20px_50px_rgba(11,26,61,0.10)] overflow-hidden ${
              imgContain ? "bg-white p-10 md:p-14" : ""
            }`}
          >
            <img
              src={img}
              alt={alt}
              className={
                imgContain
                  ? "w-full h-auto max-h-[360px] object-contain"
                  : "w-full h-[420px] md:h-[500px] object-cover"
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  const { t } = useTranslation();

  return (
    <>
      <section
        data-testid="about-header"
        className="relative overflow-hidden bg-paper border-b border-slate-200"
      >
        <div className="container-mtbl py-20 md:py-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-6">{t("about.overline")}</p>
            <h1 className="text-[#0B1A3D] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              {t("about.title")}
            </h1>
            <div className="mt-8 space-y-5 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
              <p>{t("about.introP1")}</p>
              <p data-testid="about-we-connect">{t("about.introP2")}</p>
              <p>{t("about.introP3")}</p>
            </div>
          </div>

          <div className="lg:col-span-5" data-testid="about-intro-image">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_20px_60px_rgba(11,26,61,0.10)] p-10 md:p-14 flex items-center justify-center aspect-square">
              <img
                src={ABOUT_IMG.intro}
                alt="MTBL Management Consultancy wordmark"
                className="w-full h-auto max-h-[360px] object-contain mix-blend-multiply"
              />
            </div>
          </div>
        </div>
      </section>

      <Row
        testId="about-core-values"
        overline={t("about.valuesOverline")}
        title={t("about.valuesTitle")}
        img={ABOUT_IMG.values}
        alt="Compass — Do what is right, not what is easy"
        reverse
      >
        <p>{t("about.valuesP1")}</p>
        <p>{t("about.valuesP2")}</p>
      </Row>

      <div className="bg-paper border-y border-slate-200">
        <Row
          testId="about-mission"
          overline={t("about.missionOverline")}
          title={t("about.missionTitle")}
          img={ABOUT_IMG.mission}
          alt="Business leaders meeting in the Middle East"
        >
          <p>{t("about.missionP1")}</p>
        </Row>
      </div>

      <Row
        testId="about-vision"
        overline={t("about.visionOverline")}
        title={t("about.visionTitle")}
        img={ABOUT_IMG.vision}
        alt="Global vision across Africa and the Middle East"
        reverse
      >
        <p>{t("about.visionP1")}</p>
      </Row>

      <div className="bg-paper border-y border-slate-200">
        <section className="section-pad" data-testid="about-experience">
          <div className="container-mtbl grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <p className="overline mb-5">{t("about.experienceOverline")}</p>
              <h2 className="text-[#0B1A3D] text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                {t("about.experienceTitle")}
              </h2>
              <div className="mt-6 space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
                <p>{t("about.experienceP1")}</p>
                <p>{t("about.experienceP2")}</p>
                <p>
                  {t("about.experienceP3prefix")}{" "}
                  <span className="text-[#0B1A3D] font-semibold">
                    {t("about.experienceP3highlight")}
                  </span>
                  {t("about.experienceP3suffix")}
                </p>
              </div>

              <div
                data-testid="about-experience-stats"
                className="mt-10 grid grid-cols-3 rounded-2xl border border-slate-200 bg-white overflow-hidden"
              >
                <div className="p-6 border-r border-slate-200">
                  <p className="text-[#0B1A3D] text-2xl md:text-3xl font-bold tracking-tight">
                    $100M+
                  </p>
                  <p className="overline mt-2">{t("about.statDealLabel")}</p>
                </div>
                <div className="p-6 border-r border-slate-200">
                  <p className="text-[#0B1A3D] text-2xl md:text-3xl font-bold tracking-tight">
                    5
                  </p>
                  <p className="overline mt-2">{t("about.statMarketsLabel")}</p>
                </div>
                <div className="p-6">
                  <p className="text-[#0B1A3D] text-2xl md:text-3xl font-bold tracking-tight">
                    2012
                  </p>
                  <p className="overline mt-2">
                    {t("about.statEstablishedLabel")}
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-slate-200 shadow-[0_20px_50px_rgba(11,26,61,0.10)] overflow-hidden">
                <img
                  src={ABOUT_IMG.experience}
                  alt="Team celebrating a successful delivery"
                  className="w-full h-[420px] md:h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <section data-testid="about-cta" className="section-pad bg-white">
        <div className="container-mtbl flex flex-col md:flex-row items-start md:items-center justify-between gap-8 rounded-2xl border border-slate-200 bg-paper p-10 md:p-14">
          <h3 className="text-[#0B1A3D] text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
            {t("about.ctaTitle")}
          </h3>
          <PrimaryLink to="/opportunities" testId="about-cta-opportunities">
            {t("common.viewOpportunities")}
          </PrimaryLink>
        </div>
      </section>
    </>
  );
}
